/**
 * Implied-vol skew across strikes near spot for the latest checkpoint. Graphite line; the wings are read as text.
 * Wing percentages are never shown for a null reading or one flagged `suspect` (contract item 1).
 */
export function SkewSparkline({ skew, testId = "skew-sparkline" }) {
  if (skew?.suspect) {
    return (
      <span className="sn-small sn-faint" data-testid={`${testId}-suspect`} title={skew.suspect_reason || undefined}>
        Skew withheld · suspect reading{skew.suspect_reason ? ` (${skew.suspect_reason})` : ""}
      </span>
    );
  }
  if (!skew?.points?.length) {
    return (
      <span className="sn-small sn-faint" data-testid={`${testId}-empty`}>
        Skew · not recorded for this session
      </span>
    );
  }
  const W = 120, H = 28, pad = 3;
  const ks = skew.points.map((p) => p[0]);
  const ivs = skew.points.map((p) => p[1]);
  const kMin = Math.min(...ks), kMax = Math.max(...ks);
  const ivMin = Math.min(...ivs), ivMax = Math.max(...ivs);
  const x = (k) => pad + ((k - kMin) / (kMax - kMin || 1)) * (W - 2 * pad);
  const y = (iv) => H - pad - ((iv - ivMin) / (ivMax - ivMin || 1)) * (H - 2 * pad);
  const d = skew.points.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ");
  const atmX = x(skew.spot);
  const sgn = (v) => (v > 0 ? "+" : v < 0 ? "−" : "") + Math.abs(v).toFixed(1) + "%";
  return (
    <div className="sn-row" style={{ gap: "var(--space-3)", flexWrap: "nowrap", minWidth: 0 }} data-testid={testId}
      title={`Implied-vol skew across strikes near spot (${skew.model}). Left = put wing, right = call wing, marker = ATM ${skew.spot}. A fatter left wing means downside strikes are priced as riskier.`}>
      <svg className="sn-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H, flex: "none", overflow: "visible" }} role="img"
        aria-label={`IV skew: put wing ${sgn(skew.put_wing_pct)}, call wing ${sgn(skew.call_wing_pct)} versus ATM IV ${(skew.iv_atm * 100).toFixed(1)}%`}>
        <line className="strike" x1={atmX} x2={atmX} y1={0} y2={H} />
        <path className="spark" d={d} />
        <circle className="spark-end" cx={atmX} cy={y(skew.iv_atm)} r={2.5} />
      </svg>
      <span className="sn-small sn-muted" style={{ minWidth: 0 }}>
        <span className="sn-micro" style={{ marginRight: 4 }}>Skew</span>
        put wing <b className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }} data-testid={`${testId}-put`}>{sgn(skew.put_wing_pct)}</b>
        {" · call wing "}<b className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }} data-testid={`${testId}-call`}>{sgn(skew.call_wing_pct)}</b>
        {" vs ATM IV "}<span className="sn-num" data-testid={`${testId}-atm`}>{(skew.iv_atm * 100).toFixed(1)}%</span>
      </span>
    </div>
  );
}
